import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DatabaseBackup, Download, Loader2, RefreshCw, Clock, History, Trash2, Upload } from "lucide-react";

const fmtDate = (s) => { try { return new Date(s).toLocaleString("it-IT"); } catch { return s; } };
const fmtSize = (b) => { if (!b) return "—"; const k = b / 1024; return k > 1024 ? `${(k / 1024).toFixed(1)} MB` : `${Math.round(k)} KB`; };
const STATUS = {
  completato: "bg-emerald-600 text-white",
  in_corso: "bg-amber-500 text-black",
  errore: "bg-red-600 text-white",
};

export default function Backups() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [cleanOpen, setCleanOpen] = useState(false);
  const [cleanConfirm, setCleanConfirm] = useState("");
  const [cleaning, setCleaning] = useState(false);
  const [cleanOpts, setCleanOpts] = useState({ users: true, activities: false, prospects: false });
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/backups"); setRows(r.data); } catch { /* noop */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/backups/run");
      toast.success("Backup avviato. Aggiorno tra pochi secondi…");
      setTimeout(load, 3500);
    } catch { toast.error("Impossibile avviare il backup"); }
    finally { setRunning(false); }
  };

  const download = async (b) => {
    try {
      const res = await api.get(`/backups/${b.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = b.filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download non disponibile"); }
  };

  const doRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const r = await api.post(`/backups/${restoreTarget.id}/restore`);
      const tot = Object.values(r.data.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Ripristino completato: ${tot} documenti ripristinati`);
      setRestoreTarget(null);
      setTimeout(load, 1500);
    } catch (e) {
      toast.error("Ripristino non riuscito");
    } finally { setRestoring(false); }
  };

  const doCleanup = async () => {
    setCleaning(true);
    try {
      const r = await api.post("/maintenance/cleanup-test-data", { ...cleanOpts, confirm: cleanConfirm });
      const rm = r.data.removed || {};
      toast.success(`Pulizia completata: ${rm.users_test || 0} utenti test, ${rm.activities || 0} attività, ${rm.prospects || 0} prospect rimossi`);
      setCleanOpen(false); setCleanConfirm("");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pulizia non riuscita");
    } finally { setCleaning(false); }
  };

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    if (f) setImportFile(f);
    e.target.value = "";
  };

  const doImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const r = await api.post("/backups/upload-restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const tot = Object.values(r.data.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Import completato: ${tot.toLocaleString("it-IT")} documenti ripristinati`);
      setImportFile(null);
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import non riuscito");
    } finally { setImporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="backups-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Backup & Salvataggi</h1>
          <p className="text-sm text-muted-foreground">Backup automatico notturno del database e registro dei salvataggi eseguiti.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="refresh-backups"><RefreshCw className="mr-2 h-4 w-4" /> Aggiorna</Button>
          <input type="file" ref={fileRef} accept=".gz,.json,application/gzip" className="hidden" onChange={onFilePicked} data-testid="import-file-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-file-btn"><Upload className="mr-2 h-4 w-4" /> Importa da file</Button>
          <Button onClick={runNow} disabled={running} data-testid="run-backup-btn">
            {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DatabaseBackup className="mr-2 h-4 w-4" />} Backup manuale ora
          </Button>
        </div>
      </div>

      <Card className="flex items-center gap-3 p-4" data-testid="backup-schedule-info">
        <Clock className="h-4 w-4 text-primary" />
        <span className="text-sm text-muted-foreground">Backup automatico ogni notte alle <b className="text-foreground">02:30 (ora italiana)</b>. Vengono conservati gli ultimi <b className="text-foreground">14</b> backup automatici.</span>
      </Card>

      <Card className="p-0" data-testid="backups-list-card">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Data e ora</TableHead><TableHead>Tipo</TableHead><TableHead>Stato</TableHead>
              <TableHead className="text-right">Documenti</TableHead><TableHead className="text-right">Dimensione</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((b) => (
                <TableRow key={b.id} data-testid={`backup-row-${b.id}`}>
                  <TableCell className="font-medium">{fmtDate(b.created_at)}</TableCell>
                  <TableCell><Badge variant="outline">{b.type === "auto" ? "Automatico" : "Manuale"}</Badge></TableCell>
                  <TableCell><Badge className={`border-0 ${STATUS[b.status] || ""}`}>{b.status}</Badge>{b.status === "errore" && b.error && <span className="ml-2 text-[11px] text-red-500">{b.error}</span>}</TableCell>
                  <TableCell className="text-right font-mono">{b.total_docs?.toLocaleString("it-IT") || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{fmtSize(b.size_bytes)}</TableCell>
                  <TableCell className="text-right">
                    {b.status === "completato" && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => download(b)} data-testid={`download-backup-${b.id}`}><Download className="mr-1 h-4 w-4" /> Scarica</Button>
                        <Button variant="ghost" size="sm" onClick={() => setRestoreTarget(b)} data-testid={`restore-backup-${b.id}`}><History className="mr-1 h-4 w-4" /> Ripristina</Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">Nessun backup ancora eseguito.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={!!importFile} onOpenChange={(o) => !o && !importing && setImportFile(null)}>
        <DialogContent data-testid="import-dialog">
          <DialogHeader><DialogTitle>Importa e ripristina da file</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Stai per importare il backup <b className="break-all">{importFile?.name}</b> ({fmtSize(importFile?.size)}).</p>
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-destructive">
              ⚠️ Attenzione: i dati attuali di questo ambiente (agenzie, clienti, ordini, visite, prospect, utenti, parametri) verranno <b>sostituiti</b> con quelli del file. Verrà creato automaticamente un backup di sicurezza <b>pre-import</b> prima di procedere.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportFile(null)} disabled={importing}>Annulla</Button>
            <Button variant="destructive" onClick={doImport} disabled={importing} data-testid="confirm-import-btn">
              {importing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Conferma import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <DialogContent data-testid="restore-dialog">
          <DialogHeader><DialogTitle>Ripristina backup</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Stai per ripristinare il database dal backup del <b>{restoreTarget ? fmtDate(restoreTarget.created_at) : ""}</b>.</p>
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-destructive">
              ⚠️ Attenzione: i dati attuali (agenzie, clienti, ordini, visite, prospect, utenti, parametri) verranno <b>sostituiti</b> con quelli del backup. Verrà comunque creato automaticamente un backup di sicurezza dello stato attuale prima di procedere.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreTarget(null)} disabled={restoring}>Annulla</Button>
            <Button variant="destructive" onClick={doRestore} disabled={restoring} data-testid="confirm-restore-btn">
              {restoring ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <History className="mr-2 h-4 w-4" />} Conferma ripristino
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
